import React, { useState, useRef } from 'react';
import { injectStripe } from 'react-stripe-elements';
import PropTypes from 'prop-types';
import CardSection from '../../CheckoutForm/CardSection';
import CvcSection from '../../CheckoutForm/CvcSection';
import ExpirySection from '../../CheckoutForm/ExpirySection';
import TaPricingNotification from '../../TaPricingNotification';
import { interpretStripeCardError } from '../../../utils/payments';
import { MONTHLY_TO_ANNUAL_CONTENT } from './constants';

import styles from '../../Form/themeV3.scss';

const stripeInputStyle = {
    base: {
        fontFamily: '\'Inter\', Arial, sans-serif',
        fontSize: '16px',
        fontWeight: 500,
        color: '#2b3339',
        '::placeholder': {
            fontSize: '14px',
            color: '#b9c5cd',
        },
    },
};

const ChangeCardForm = ({
    stripe,
    onSuccess,
    onError,
    submitting,
    disabled,
    formStyles,
    onUseCardOnFile,
    isTennisAustraliaMember,
    savingsPercentage,
    renderCardBrands,
    ctaText,
    errorMessage,
}) => {
    const [localSubmitting, setLocalSubmitting] = useState(false);
    const [cardholderName, setCardholderName] = useState('');
    const [error, setError] = useState({
        name: null,
        cardNumber: null,
        cardCvc: null,
        cardExpiry: null,
    });
    const cardNumberRef = useRef(null);

    const handleCardElementReady = (element) => {
        cardNumberRef.current = element;
    };

    const handleChange = (ev) => {
        if (ev.elementType) {
            if (ev.error) {
                setError((prev) => ({ ...prev, [ev.elementType]: ev.error.message }));
            } else {
                setError((prev) => ({ ...prev, [ev.elementType]: null }));
            }
        }
    };

    const handleNameChange = (e) => {
        const newName = e.target.value;
        const nameError = !newName || newName.trim() === '' ? MONTHLY_TO_ANNUAL_CONTENT.nameValidationError : null;
        setCardholderName(newName);
        setError((prev) => ({ ...prev, name: nameError }));
    };

    const isSubmitting = localSubmitting || submitting;

    const handleSubmit = async (e) => {
        e.preventDefault();

        const nameError = !cardholderName || cardholderName.trim() === '' ? MONTHLY_TO_ANNUAL_CONTENT.nameValidationError : null;
        setError((prev) => ({ ...prev, name: nameError }));
        if (nameError) {
            return;
        }

        setLocalSubmitting(true);

        const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardNumberRef.current,
            billing_details: {
                name: cardholderName.trim(),
            },
        });

        if (stripeError) {
            setLocalSubmitting(false);
            onError(interpretStripeCardError(stripeError));
        } else {
            await onSuccess(paymentMethod.id);
            setLocalSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} id="upgrade-monthly-change-card-form">
            <div className={formStyles?.formTitle || ''}>{MONTHLY_TO_ANNUAL_CONTENT.changeCardInstruction}</div>
            {errorMessage && (
                <div className="is-invalid mb16" data-test="change-card-form-error">
                    <div className="invalid-feedback">{errorMessage}</div>
                </div>
            )}
            <div className={`form-group form-group-xl ${error.name ? 'is-invalid' : ''} ${styles.themeV3}`}>
                <label htmlFor="upgrade-monthly-cardholder-name" />
                <div className={`${styles.inputWrapper} input-wrapper`}>
                    <input
                        id="upgrade-monthly-cardholder-name"
                        type="text"
                        className={`${styles.formControl} form-control`}
                        value={cardholderName}
                        onChange={handleNameChange}
                        disabled={disabled || isSubmitting}
                    />
                    <label>{MONTHLY_TO_ANNUAL_CONTENT.cardholderNameLabel}</label>
                    <div className="error-indicator" />
                </div>
                {error.name && <div className="invalid-feedback">{error.name}</div>}
            </div>
            <div className="row">
                <CardSection
                    error={error.cardNumber}
                    onChange={handleChange}
                    style={stripeInputStyle}
                    onCardElementReady={handleCardElementReady}
                    themeV3
                    disabled={disabled || isSubmitting}
                />
            </div>
            <div className="row">
                <CvcSection
                    error={error.cardCvc}
                    onChange={handleChange}
                    style={stripeInputStyle}
                    themeV3
                    disabled={disabled || isSubmitting}
                />
                <ExpirySection
                    error={error.cardExpiry}
                    onChange={handleChange}
                    style={stripeInputStyle}
                    themeV3
                    disabled={disabled || isSubmitting}
                />
            </div>
            {isTennisAustraliaMember && (
                <TaPricingNotification savingsPercentage={savingsPercentage || 0} />
            )}
            {renderCardBrands?.()}
            {onUseCardOnFile && (
                <button
                    type="button"
                    className={formStyles?.changeCardLink || ''}
                    onClick={onUseCardOnFile}
                    data-testid="change-card-form.use-card-on-file-btn"
                >
                    {MONTHLY_TO_ANNUAL_CONTENT.useCardOnFile}
                </button>
            )}
            <button
                type="submit"
                className={`${formStyles?.upgradeButton || ''} ${isSubmitting ? 'text-dark' : ''}`}
                disabled={disabled || isSubmitting}
                data-test="new-card-submit"
                data-testid="change-card-form.submit-btn"
            >
                {isSubmitting && (<i className="fas fa-circle-notch fa-spin mr8" />)}
                {isSubmitting ? MONTHLY_TO_ANNUAL_CONTENT.ctaProcessing : (ctaText || MONTHLY_TO_ANNUAL_CONTENT.ctaText)}
            </button>
            <div className={formStyles?.disclaimer || ''}>
                {MONTHLY_TO_ANNUAL_CONTENT.disclaimer.prefix}
                <a href={MONTHLY_TO_ANNUAL_CONTENT.disclaimer.termsHref} target="_blank" rel="noopener noreferrer" data-testid="change-card-form.terms-link">
                    {MONTHLY_TO_ANNUAL_CONTENT.disclaimer.termsLink}
                </a>
                {MONTHLY_TO_ANNUAL_CONTENT.disclaimer.suffix}
            </div>
        </form>
    );
};

ChangeCardForm.propTypes = {
    stripe: PropTypes.object.isRequired,
    onSuccess: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    submitting: PropTypes.bool,
    disabled: PropTypes.bool,
    formStyles: PropTypes.object,
    onUseCardOnFile: PropTypes.func,
    isTennisAustraliaMember: PropTypes.bool,
    savingsPercentage: PropTypes.number,
    renderCardBrands: PropTypes.func,
    ctaText: PropTypes.string,
    errorMessage: PropTypes.string,
};

ChangeCardForm.defaultProps = {
    submitting: false,
    disabled: false,
    formStyles: {},
    onUseCardOnFile: null,
    isTennisAustraliaMember: false,
    savingsPercentage: 0,
    renderCardBrands: null,
    ctaText: null,
    errorMessage: null,
};

export default injectStripe(ChangeCardForm);
